import apiService from "@/core/services/api/api.service";
import { HttpResponse } from "@/core/types/http.types";
import {
  IAccessRequestPayload,
  ILoginRequest,
  ILoginResponse,
  IUser,
  IUserResponse,
} from "@/core/models/user.model";
import { setApiCallsAuth } from "@/core/plugins/axios";
import authService from "@/core/services/auth/auth.service";
import { EMPTY_STRING } from "@/core/constants";
import { DEFAULT_USER } from "../../constants";
import { userUrls } from "@/core/services/api/urls";

// action types
export const GET_USER = "getUserData";
export const LOGIN = "login";
export const LOGOUT = "logout";
export const REQUEST_ACCESS = "sendLabResultsForAccess";

// mutation types
export const SET_USER = "setUserData";
export const UPDATE_USER = "updateUserData";
export const PURGE_AUTH = "logOut";
export const SET_AUTH = "setUser";
export const SET_ACCESS = "setUserHasAccessPanel";

const state: IUser = {
  ...DEFAULT_USER,
  isAuthenticated: Boolean(authService.getToken()),
};

const getters = {
  user(state: IUser) {
    return state;
  },
  userFullName(state: IUser) {
    return `${state.firstname} ${state.lastname}`;
  },
  isAuthenticated(state: IUser) {
    return state.isAuthenticated;
  },
  hasPanelAccess(state: IUser) {
    return state.hasPanelAccess;
  },
};

const actions = {
  async [GET_USER](context: any) {
    try {
      const response = await apiService.request({
        request: {
          url: userUrls.getUser,
          method: "GET",
        },
      });

      const { data } = response;
      context.commit(SET_USER, data.data);

      return response;
    } catch (error: any) {
      throw { ...error } as HttpResponse;
    }
  },
  async [LOGIN](context: any, payload: ILoginRequest) {
    try {
      const response = await apiService.request({
        request: {
          url: userUrls.login,
          method: "POST",
          data: payload,
        },
      });

      const { data } = response;
      context.commit(SET_AUTH, data);

      return response;
    } catch (error: any) {
      throw { ...error } as HttpResponse;
    }
  },
  async [LOGOUT](context: any) {
    try {
      const response = await apiService.request({
        request: {
          url: userUrls.logout,
          method: "POST",
        },
      });

      window.location.href = "/login";
      context.commit(PURGE_AUTH);

      return response;
    } catch (error: any) {
      throw { ...error } as HttpResponse;
    }
  },
  async [REQUEST_ACCESS](context: any, payload: IAccessRequestPayload) {
    try {
      const response = await apiService.request({
        request: {
          url: userUrls.requestAccess,
          method: "POST",
          data: {
            metallization: Number(payload.metallization),
            carbon: Number(payload.carbon),
            production_rate: Number(payload.productionRate),
          },
        },
      });

      context.commit(SET_ACCESS, true);

      return response;
    } catch (error: any) {
      throw { ...error } as HttpResponse;
    }
  },
};

const mutations = {
  [SET_USER](state: IUser, response: IUserResponse) {
    state.personalId = response.personal_id;
    state.firstname = response.first_name || EMPTY_STRING;
    state.lastname = response.last_name || EMPTY_STRING;
    state.phoneNumber = response.phone_number;
    state.workSchedule = response.work_schedule;
  },
  [UPDATE_USER](state: IUser, data: Partial<IUser>) {
    state = { ...state, ...data };
  },
  [SET_AUTH](state: IUser, response: ILoginResponse) {
    state.isAuthenticated = true;
    setApiCallsAuth(response.token);
    authService.saveAuth(response.token);
  },
  [PURGE_AUTH](state: IUser) {
    state.isAuthenticated = false;
    state = DEFAULT_USER;
    authService.destroyAuth();
  },
  [SET_ACCESS](state: IUser, access: boolean) {
    state.hasPanelAccess = access;
  },
};

export default {
  state,
  actions,
  mutations,
  getters,
};
